import { useMemo, useState } from 'react'
import { Check, ImageIcon, Loader2, Sparkles } from 'lucide-react'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { Image } from 'src/shared/components/ui'
import { Textarea } from 'src/shared/components/ui/textarea'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'

/** Shared generate form used inside the mockups dialog. */
export const MockupGenerateForm = ({
  designUrl,
  onGenerate,
  isPending = false,
  canGenerate = true,
  disabledReason,
  hasMockups = false,
  compact = false,
}: {
  designUrl?: string | null
  onGenerate?: (params: { logoUrl: string; customPrompt: string }) => void
  isPending?: boolean
  canGenerate?: boolean
  disabledReason?: string
  hasMockups?: boolean
  compact?: boolean
}) => {
  const { t } = useLocalization()
  const { organization } = useOrgsApi()
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null)
  const [customPrompt, setCustomPrompt] = useState('')

  const icons = useMemo(() => {
    const org = organization?.data
    const urls = [
      org?.chosen_logo_url,
      ...(org?.logo_urls?.split(',') ?? []),
      designUrl,
    ]
    return [
      ...new Set(urls.map((url) => url?.trim()).filter(Boolean)),
    ] as string[]
  }, [organization?.data, designUrl])

  const activeIcon = selectedIcon ?? icons[0]
  const generateDisabled = !canGenerate || !activeIcon || isPending

  const handleGenerate = () => {
    if (!activeIcon || !onGenerate || generateDisabled) return
    onGenerate({ logoUrl: activeIcon, customPrompt })
  }

  return (
    <Column className={cn('gap-3', compact && 'gap-2.5')}>
      <Column className="gap-1.5">
        <p className="text-[11px] font-medium tracking-wide text-gray-500">
          {t('Artwork', 'Artwork')}
        </p>

        {icons.length === 0 ? (
          <Row className="items-center gap-2.5 rounded-xl border border-dashed border-gray-200 bg-white/70 px-3 py-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100">
              <ImageIcon className="h-4 w-4 text-gray-400" />
            </div>
            <p className="text-[12px] text-gray-500">
              {t(
                'Add a logo in organization settings to generate.',
                'Lägg till en logotyp i organisationsinställningarna för att generera.'
              )}
            </p>
          </Row>
        ) : (
          <Row className="flex-wrap gap-2">
            {icons.map((url) => {
              const isSelected = url === activeIcon
              return (
                <button
                  key={url}
                  type="button"
                  onClick={() => setSelectedIcon(url)}
                  className={cn(
                    'relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-white transition-all',
                    isSelected
                      ? 'border-blue-500 shadow-md ring-2 ring-blue-500/25 scale-[1.02]'
                      : 'border-gray-200/80 shadow-sm hover:border-gray-300 hover:shadow'
                  )}
                >
                  <Image
                    src={url}
                    alt={t('Icon', 'Ikon')}
                    className="h-full w-full object-contain p-1.5"
                  />
                  {isSelected && (
                    <span className="absolute right-1 top-1 rounded-full bg-blue-500 p-0.5 shadow-sm">
                      <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                </button>
              )
            })}
          </Row>
        )}
      </Column>

      <Column className="gap-1.5">
        <p className="text-[11px] font-medium tracking-wide text-gray-500">
          {t('Direction', 'Riktning')}
          <span className="ml-1 font-normal text-gray-400">
            {t('(optional)', '(valfritt)')}
          </span>
        </p>
        <Textarea
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          placeholder={t(
            'e.g. embroidered on the chest, soft studio light…',
            't.ex. broderad på bröstet, mjuk studioljus…'
          )}
          rows={compact ? 2 : 2}
          className="min-h-0 resize-none rounded-xl border-gray-200/80 bg-white/80 px-3 py-2.5 text-[12px] shadow-sm placeholder:text-gray-400"
        />
      </Column>

      {disabledReason && !canGenerate && (
        <p className="text-[11px] text-amber-700/80">{disabledReason}</p>
      )}

      <Button
        variant="primary"
        size="sm"
        className="h-9 w-full text-[12px] font-medium"
        disabled={generateDisabled}
        onClick={handleGenerate}
      >
        {isPending ? (
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        ) : (
          <Sparkles className="mr-1.5 h-3.5 w-3.5" />
        )}
        {hasMockups
          ? t('Generate more', 'Generera fler')
          : t('Generate mockups', 'Generera mockups')}
        {!isPending && (
          <span className="ml-1.5 text-[10px] font-normal opacity-70">
            ~15s
          </span>
        )}
      </Button>
    </Column>
  )
}
